import { damagePlayer } from './player';
import { playBombExplosionSound } from './audio';

// Frame layout (33×33 per frame, 9 cols × 3 rows):
// Row 0 (0–8):  Walk 01–04, Skid, Stand 01–02, [empty ×2]
// Row 1 (9–17): Brace 01–05, Wait 01–04
// Row 2 (18–26): Explode 01–03, [empty ×6]
const FRAMES = {
  walkStart:    0,
  walkEnd:      3,
  skid:         4,
  stand:        5,
  braceStart:   9,
  braceEnd:     13,
  waitStart:    14,
  waitEnd:      17,
  explodeStart: 18,
  explodeEnd:   20,
};

const DETECT_RANGE  = 100;
const BRACE_RANGE   = 70;
const EXPLODE_RANGE = 90;
const WALK_SPEED    = 45;

const CONFIGS = [
  { x: 600, y: 127, patrolLeft: 540, patrolRight: 700 },
];

export function createBombs(scene) {
  scene.bombs = [];

  scene.anims.create({
    key: 'bombWalk',
    frames: scene.anims.generateFrameNumbers('bomb', { start: FRAMES.walkStart, end: FRAMES.walkEnd }),
    frameRate: 8,
    repeat: -1
  });
  scene.anims.create({
    key: 'bombBrace',
    frames: scene.anims.generateFrameNumbers('bomb', { start: FRAMES.braceStart, end: FRAMES.braceEnd }),
    frameRate: 4,
    repeat: 0
  });
  scene.anims.create({
    key: 'bombWait',
    frames: scene.anims.generateFrameNumbers('bomb', { start: FRAMES.waitStart, end: FRAMES.waitEnd }),
    frameRate: 2,
    repeat: 0
  });
  scene.anims.create({
    key: 'bombExplode',
    frames: scene.anims.generateFrameNumbers('bomb', { start: FRAMES.explodeStart, end: FRAMES.explodeEnd }),
    frameRate: 6,
    repeat: 0
  });

  for (const cfg of CONFIGS) addBomb(scene, cfg);
}

function addBomb(scene, cfg) {
  const bomb = scene.physics.add.sprite(cfg.x, cfg.y, 'bomb', FRAMES.stand);
  bomb.setScale(1);
  bomb.setCollideWorldBounds(true);
  bomb.body.setSize(20, 28);
  bomb.body.setOffset(6, 4);
  bomb.setDepth(5);

  bomb.health      = 2;
  bomb.isDying     = false;
  bomb.hasBraced   = false;
  bomb.state       = 'idle';
  bomb.patrolLeft  = cfg.patrolLeft;
  bomb.patrolRight = cfg.patrolRight;
  bomb.direction   = 1;

  bomb.hit = (_facing) => hitBomb(scene, bomb);

  scene.physics.add.collider(bomb, scene.environmentLayer);

  scene.physics.add.overlap(scene.player, bomb, () => {
    if (bomb.isDying || bomb.state === 'explode') return;
    const alreadyInvincible = scene.playerInvincible;
    damagePlayer(scene, 0.5);
    if (scene.player.isDead) return;
    if (alreadyInvincible) return;
    if (scene.player.isClimbing || scene.player.isPerched) return;
    const knockDir = scene.player.x < bomb.x ? -1 : 1;
    scene.player.setVelocityX(knockDir * 180);
    scene.player.knockbackTimer = 300;
  }, null, scene);

  scene.bombs.push(bomb);
}

function hitBomb(scene, bomb) {
  if (bomb.isDying) return;
  // Hitting during brace/wait triggers early explosion
  if (bomb.state === 'brace' || bomb.state === 'wait') {
    explodeBomb(scene, bomb);
    return;
  }
  bomb.health -= 1;
  scene.tweens.add({
    targets: bomb,
    alpha: 0.2,
    duration: 60,
    yoyo: true,
    repeat: 3,
    onComplete: () => bomb?.active && bomb.setAlpha(1)
  });
  if (bomb.health <= 0) explodeBomb(scene, bomb);
}

function startBrace(scene, bomb) {
  if (['brace', 'wait', 'explode'].includes(bomb.state) || bomb.isDying || bomb.hasBraced) return;
  bomb.hasBraced = true;
  bomb.state = 'brace';
  bomb.setVelocityX(0);
  bomb.setFrame(FRAMES.skid);
  scene.time.delayedCall(200, () => {
    if (!bomb?.active || bomb.isDying) return;
    bomb.anims.play('bombBrace');
    bomb.once('animationcomplete-bombBrace', () => {
      if (!bomb?.active || bomb.isDying) return;
      bomb.state = 'wait';
      bomb.anims.play('bombWait');
      bomb.once('animationcomplete-bombWait', () => {
        if (!bomb?.active || bomb.isDying) return;
        explodeBomb(scene, bomb);
      });
    });
  });
}

function explodeBomb(scene, bomb) {
  if (bomb.isDying) return;
  bomb.isDying = true;
  bomb.state = 'explode';
  bomb.setVelocityX(0);
  playBombExplosionSound();
  bomb.anims.play('bombExplode');
  // AoE damage if rat is nearby
  const dx = Math.abs(scene.player.x - bomb.x);
  const dy = Math.abs(scene.player.y - bomb.y);
  if (dx < EXPLODE_RANGE && dy < EXPLODE_RANGE) {
    damagePlayer(scene, 1);
  }
  bomb.once('animationcomplete-bombExplode', () => {
    scene.time.delayedCall(200, () => bomb?.destroy());
  });
}

export function updateBombs(scene) {
  for (const bomb of scene.bombs) {
    if (!bomb.active || bomb.isDying) continue;
    if (['brace', 'wait', 'explode'].includes(bomb.state)) continue;

    const distanceX = scene.player.x - bomb.x;
    const absDistX  = Math.abs(distanceX);
    const absDistY  = Math.abs(scene.player.y - bomb.y);

    if (bomb.state === 'idle') {
      if (absDistX < DETECT_RANGE && absDistY < 48) {
        bomb.state = 'chase';
        bomb.anims.play('bombWalk', true);
      } else {
        if (bomb.x <= bomb.patrolLeft || bomb.body.blocked.left)  bomb.direction =  1;
        else if (bomb.x >= bomb.patrolRight || bomb.body.blocked.right) bomb.direction = -1;
        bomb.setVelocityX(bomb.direction * WALK_SPEED);
        bomb.flipX = bomb.direction > 0;
        bomb.anims.play('bombWalk', true);
      }
      continue;
    }

    // Chase
    if (absDistX < BRACE_RANGE && absDistY < 48) {
      startBrace(scene, bomb);
      continue;
    }

    if (!bomb.hasBraced && (absDistX > DETECT_RANGE * 1.5 || absDistY > 48)) {
      bomb.state = 'idle';
      continue;
    }

    bomb.setVelocityX(Math.sign(distanceX) * WALK_SPEED);
    bomb.flipX = distanceX > 0;
  }

  scene.bombs = scene.bombs.filter(b => b.active);
}
